import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Button,
  MenuItem
} from '@material-ui/core';
import { TextField, Select } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { addUser } from '../../redux/actions/userActions';
import CliniqueService from '../../services/CliniqueService';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from "react-toastify";

// Icônes Material-UI
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PersonIcon from '@material-ui/icons/Person';
import LockIcon from '@material-ui/icons/Lock';
import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(0.5),
  },
  gridItem: {
    padding: theme.spacing(0.3),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(0.8),
    fontSize: '16px !important',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '8px 24px',
    borderRadius: 6,
    boxShadow: '0 3px 10px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(0.8),
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 4px 15px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.8),
  },
  formTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(0.3),
    fontSize: '1.1rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.75rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(0.8),
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.4),
  },
  compactField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.8rem',
      minHeight: '40px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.7rem',
      marginTop: 2,
    },
  },
  selectField: {
    '& .MuiSelect-root': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.8rem',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
  },
}));

const UserForm = ({ onClose, refresh }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const refreshRoles = async () => {
      try {
        const response = await CliniqueService.getRoles();
        setRoles(response.data || []);
      } catch (error) {
        console.error('Erreur chargement rôles:', error);
        toast.error("Erreur chargement rôles");
      }
    };
    refreshRoles();
  }, [dispatch]);

  const passwordRegExp = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

  const initialValues = {
    speudo: '',
    nom: '',
    prenom: '',
    rol: '',
    passwd: '',
    confirmPasswd: ''
  };

  const validationSchema = Yup.object().shape({
    speudo: Yup.string().min(3, "Trop court").required("Requis"),
    nom: Yup.string().required("Requis"),
    prenom: Yup.string().required("Requis"),
    rol: Yup.string().required("Requis"),
    passwd: Yup.string()
      .min(8, "Minimum 8 caractères")
      .matches(passwordRegExp, "Doit contenir majuscule, minuscule, chiffre et symbole")
      .required('Requis'),
    confirmPasswd: Yup.string()
      .oneOf([Yup.ref('passwd')], "Mots de passe différents")
      .required('Requis')
  });

  const onSubmit = async (values, props) => {
    try {
      const user = {
        "speudo": values.speudo.trim(),
        "nom": values.nom.trim(),
        "prenom": values.prenom.trim(),
        "rol": values.rol,
        "passwd": values.passwd
      };

      await dispatch(addUser(user));
      
      toast.success("✅ Utilisateur créé avec succès", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      
      props.resetForm();
      
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      toast.error("❌ Erreur création utilisateur", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête compact */}
      <Box className={classes.formHeader}>
        <PersonAddIcon style={{ fontSize: 28, color: '#226D68', marginBottom: 2 }} />
        <div className={classes.formTitle}>Nouvel Utilisateur</div>
        <div className={classes.formSubtitle}>Créez un nouveau compte utilisateur</div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={0.8}>
              
              {/* Informations personnelles */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <PersonIcon style={{ fontSize: 16 }} /> Informations
                </div>
              </Grid>

              {/* Username */}
              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='speudo' 
                  label='Username' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  className={classes.compactField}
                  error={props.errors.speudo && props.touched.speudo}
                  helperText={<ErrorMessage name='speudo' />}
                  required
                  InputProps={{
                    startAdornment: <PersonIcon className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Nom et Prénom sur une ligne */}
              <Grid item xs={12} container spacing={0.8}>
                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    name='nom' 
                    label='Nom' 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    className={classes.compactField}
                    error={props.errors.nom && props.touched.nom}
                    helperText={<ErrorMessage name='nom' />}
                    required
                  />
                </Grid>

                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    name='prenom' 
                    label='Prénom' 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    className={classes.compactField}
                    error={props.errors.prenom && props.touched.prenom}
                    helperText={<ErrorMessage name='prenom' />}
                    required
                  />
                </Grid>
              </Grid>

              {/* Rôle */}
              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={Select}
                  name="rol"
                  label="Rôle"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={`${classes.compactField} ${classes.selectField}`}
                  error={props.errors.rol && props.touched.rol}
                  helperText={<ErrorMessage name='rol' />}
                  required
                  displayEmpty
                  inputProps={{
                    startAdornment: <SecurityIcon className={classes.iconAdornment} />
                  }}
                >
                  <MenuItem value="" disabled>
                    <SecurityIcon style={{ marginRight: 8, color: '#226D68', fontSize: 16 }} />
                    Sélectionnez un rôle
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.rol} value={role.rol}>
                      {role.rol}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              {/* Mot de passe */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <LockIcon style={{ fontSize: 16 }} /> Sécurité
                </div>
              </Grid>

              {/* Mot de passe et confirmation sur une ligne */}
              <Grid item xs={12} container spacing={0.8}>
                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    type="password" 
                    name="passwd" 
                    label='Mot de passe' 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    className={classes.compactField}
                    error={props.errors.passwd && props.touched.passwd}
                    helperText={<ErrorMessage name='passwd' />}
                    required
                    InputProps={{
                      startAdornment: <LockIcon className={classes.iconAdornment} />
                    }}
                  />
                </Grid>

                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    type="password" 
                    name="confirmPasswd" 
                    label='Confirmation' 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    className={classes.compactField}
                    error={props.errors.confirmPasswd && props.touched.confirmPasswd}
                    helperText={<ErrorMessage name='confirmPasswd' />}
                    required
                    InputProps={{
                      startAdornment: <LockIcon className={classes.iconAdornment} />
                    }}
                  />
                </Grid>
              </Grid>

              {/* Information sécurité */}
              <Grid item xs={12}>
                <Box style={{ 
                  padding: '6px 10px', 
                  backgroundColor: '#f0f9ff', 
                  border: '1px solid #e0f2fe',
                  borderRadius: '4px',
                  color: '#0369a1',
                  fontSize: '0.65rem',
                  lineHeight: '1.2',
                }}>
                  <strong>⚠️ Sécurité:</strong> 8 caractères min, majuscule, minuscule, chiffre, symbole
                </Box>
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<PersonAddIcon style={{ fontSize: 16 }} />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting ? 'Création...' : 'Créer utilisateur'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default UserForm;